import { useInfiniteQuery } from '@tanstack/react-query';

import transactionsClient from '@clients/transactionsClient';
import notifyAPIError from '@utils/notifyAPIError';

const { getTransactions } = transactionsClient();

export type StatementTransactionsProps = {
    accountId: number;
    statementId?: number;
    pageSize?: number;
    order?: string;
    customerId?: number;
    credit?: boolean;
    authorizationType?: string;
    processingCode?: string;
    transactionTypeId?: number;
    statementPost?: boolean;
    authorizationTrackingId?: string;
    authorizationId?: number;
    eventDateStart?: string;
    eventDateEnd?: string;
};

const useGetTransactions = ({
    statementId = 0,
    accountId,
    pageSize = 5,
    order = 'desc',
    customerId,
    credit,
    authorizationType,
    processingCode,
    transactionTypeId,
    statementPost,
    authorizationTrackingId,
    authorizationId,
    eventDateStart,
    eventDateEnd,
}: StatementTransactionsProps) => {
    const fetchTransactions = (pageParam: number | undefined) => {
        return getTransactions({
            accountId,
            ...(statementId && { statementId }),
            pageOffset: pageParam,
            pageSize,
            order,
            customerId,
            credit,
            authorizationType,
            processingCode,
            transactionTypeId,
            statementPost,
            authorizationTrackingId,
            authorizationId,
            eventDateStart,
            eventDateEnd,
        });
    };
    const {
        data,
        error,
        isError,
        isLoading,
        fetchNextPage,
        hasNextPage,
        refetch,
    } = useInfiniteQuery(
        [
            'transactions',
            accountId,
            {
                statementId,
                order,
                pageSize,
                customerId,
                credit,
                authorizationType,
                processingCode,
                transactionTypeId,
                statementPost,
                authorizationId,
                eventDateStart,
                eventDateEnd,
            },
        ],
        ({ pageParam }) => fetchTransactions(pageParam),
        {
            getNextPageParam: (lastPage, allPages): number | undefined =>
                lastPage?.has_next ? allPages.length : undefined,
            onError: notifyAPIError,
        },
    );

    return {
        data,
        error,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isError,
        refetch,
    };
};

export default useGetTransactions;
